import React, { Fragment, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, useApolloClient } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { mapProps } from 'recompose';
import { connect } from 'react-redux';
import produce from 'immer';

import {
  getImageSources,
  getFaceboxOutlineStyleFromDOM,
} from '../../../../util/photo';
import { VOTE_DECISION } from '../../../../constants/vote-decisions';
import Button from '../../../../components/global/Button';
import { IdentityForFaceTag, PhotoForFaceTag } from './graphql/queries';
import FaceTagIdentitySelection, {
  FACE_TAG_IDENTITY_SELECTION_MODAL,
} from './components/FaceTagIdentitySelection';
import Spinner from '../../../../components/global/Spinner';
import {
  openModal as openModalAction,
  openAlert as openAlertAction,
} from '../../../../actions/modal-actions';
import { FAUX_FACE_TYPE } from '../../../../constants/face';
import { BulkVote, IdentityPubBoxCreate } from './graphql/mutations';
import { getErrorString } from '../../../../util/normalize-errors';
import { MeQuery } from '../../../../graphql/queries/account';
import { getIdentityFullName } from '../../../../util/identity';
import CheckmarkIcon from '../../../../components/icons/CheckmarkIcon';
import ArrowLeft from '../../../../components/icons/ArrowLeft';
import { ACCOUNT_TYPE } from '../AlbumFolder/AlbumFolders/AlbumFolderMembers/components/AlbumFolderIdentityMembershipDetails/constants';

const propTypes = {
  userId: PropTypes.string.isRequired,
  photoId: PropTypes.string.isRequired,
  albumId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  photo: PropTypes.shape({
    id: PropTypes.string,
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
    ),
  }),
  faces: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      facebox: PropTypes.array,
      has_human_votes: PropTypes.bool,
    }),
  ),
  bulkVote: PropTypes.func.isRequired,
  identityPubBoxCreate: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  openAlert: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onPrevRecord: PropTypes.func,
  onNextRecord: PropTypes.func,
  prevRecordId: PropTypes.string,
  nextRecordId: PropTypes.string,
  onFaceTagTotallyVoteCast: PropTypes.func,
  onFaceTagNopeVoteCast: PropTypes.func,
};

const FaceTagPhoto = ({
  userId,
  albumId,
  photoId,
  loading,
  photo,
  faces,
  bulkVote,
  identityPubBoxCreate,
  openModal,
  openAlert,
  onClose,
  onPrevRecord,
  onNextRecord,
  prevRecordId,
  nextRecordId,
  onFaceTagTotallyVoteCast,
  onFaceTagNopeVoteCast,
}) => {
  const client = useApolloClient();
  const image = useRef(null);
  const wrapper = useRef(null);

  const [imageLoaded, setImageLoaded] = useState(false);
  const [updating, setUpdating] = useState(false);

  const thumbs = photo?.thumbnail_urls;

  const { src, srcSet } = getImageSources(thumbs);

  useEffect(() => {
    setImageLoaded(false);
  }, [photoId]);

  const onSelectIdentity = async ({
    face,
    identity,
    decision,
    humanLabeledPubBox,
  }) => {
    setUpdating(true);

    try {
      const { current_face: currentFace, current_matching_face: matchingFace } =
        identity ?? {};

      if (!humanLabeledPubBox) {
        await bulkVote({
          identityId: identity.id,
          faceId: face.id,
          photoId,
          userId,
          selfieId: currentFace?.id ?? matchingFace?.id,
          identity,
          face,
          decision,
        });

        if (decision === VOTE_DECISION.TOTALLY && onFaceTagTotallyVoteCast) {
          onFaceTagTotallyVoteCast();
        }

        if (decision === VOTE_DECISION.NOPE) {
          const {
            data: { identity: identityForRosterIdentityCheck },
          } = await client.query({
            query: IdentityForFaceTag,
            variables: {
              id: identity.id,
            },
            fetchPolicy: 'network-only',
          });
          const marketingAccountLinkedIdentity = identityForRosterIdentityCheck?.roster_identity?.linked_identities?.records?.find(
            ({ identity: linkedIdentityIdentity }) =>
              linkedIdentityIdentity?.account?.type === ACCOUNT_TYPE.MARKETING,
          );

          if (marketingAccountLinkedIdentity) {
            const { identity: linkedIdentity } = marketingAccountLinkedIdentity;

            await bulkVote({
              identityId: linkedIdentity.id,
              faceId: face.id,
              photoId,
              userId,
              selfieId:
                linkedIdentity?.current_face?.id ??
                linkedIdentity?.current_matching_face?.id,
              identity: linkedIdentity,
              face,
              decision: VOTE_DECISION.NOPE,
            });
          }
          onFaceTagNopeVoteCast?.();
        }
      } else if (!face.id) {
        await identityPubBoxCreate({
          identityId: identity.id,
          photoId,
          box: face.box,
        });
      }
    } catch (err) {
      openAlert({
        message:
          'There was an error attempting to toggle a photo highlight. Please try again later.',
        info: getErrorString(err),
      });
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    if (photo && !thumbs) {
      openAlert({
        message: 'This photo is still being processed. Please come back later',
      });

      onClose();
    }
  }, [photo]);

  const createPubBox = event => {
    const {
      clientX,
      clientY,
      target: { clientWidth: width, clientHeight: height },
    } = event;
    const extraWidth = (wrapper.current?.offsetWidth - width) / 2;
    const extraHeight = (wrapper.current?.offsetHeight - height) / 2;
    const box = [
      (clientX - extraWidth) / width,
      (clientY - extraHeight) / height,
      0.05,
      0.05,
      0,
    ];
    const pubBox = {
      box,
      has_human_votes: false,
      matched_photos: {
        records: [],
      },
    };

    openModal(FACE_TAG_IDENTITY_SELECTION_MODAL, {
      face: pubBox,
      humanLabeledPubBox: true,
    });
  };

  return (
    <div className="face-tag-photo-viewer">
      <div className="face-tag-photo-viewer-header">
        <div className="face-tag-photo-viewer-header-title">Tag faces</div>
        {onClose && (
          <Button
            btnSize="sm"
            btnStyle="reset"
            onClick={onClose}
            disabled={updating}
            className="face-tag-close-btn"
          >
            <CheckmarkIcon color="#fff" size={12} /> Done
          </Button>
        )}
      </div>
      {loading && (
        <div className="min-h-10 0vh d-flex align-items-center justify-content-center m-auto">
          <Spinner />
        </div>
      )}
      {!loading && (
        <Fragment>
          <div ref={wrapper} className="face-tag-photo-viewer-image-wrapper">
            {onPrevRecord && (
              <button
                className="face-tag-viewer-nav face-tag-viewer-nav-prev"
                onClick={onPrevRecord}
                disabled={!prevRecordId}
              >
                <span className="face-tag-viewer-nav-outline">
                  <ArrowLeft color="#fff" />
                </span>
              </button>
            )}
            <img
              role="presentation"
              className="face-tag-photo-viewer-image"
              src={src}
              srcSet={srcSet}
              alt="face-tag"
              ref={image}
              onLoad={() => setImageLoaded(true)}
              onClick={createPubBox}
            />
            {imageLoaded &&
              faces.map(face => {
                const [matchedPhoto] = face.matched_photos?.records ?? [];
                const isPubBox =
                  face.faux_face_type === FAUX_FACE_TYPE.HAND_LABELED_PUB &&
                  face.is_creator;
                const faceboxClasses = classNames({
                  'face-tag-photo-facebox': true,
                  'face-tag-photo-facebox-no-border': isPubBox,
                  'face-tag-photo-facebox-machine-vote':
                    !face.has_human_votes && !!matchedPhoto && !isPubBox,
                  'face-tag-photo-facebox-human-vote':
                    face.has_human_votes && !isPubBox,
                });

                return (
                  <div
                    className={faceboxClasses}
                    style={getFaceboxOutlineStyleFromDOM({
                      facebox: face.facebox,
                      imageElement: image.current,
                      wrapperElement: wrapper.current,
                      // hideBox: isPubBox,
                    })}
                    key={face.id}
                  >
                    {!isPubBox && (
                      <Button
                        btnStyle="reset"
                        onClick={() => {
                          openModal(FACE_TAG_IDENTITY_SELECTION_MODAL, {
                            face,
                            currentIdentity: matchedPhoto?.identity,
                          });
                        }}
                        disabled={updating}
                        className="photo-highlight-facebox-face"
                        data-testid={`face-tag-photo-${face.id}`}
                      />
                    )}
                    {matchedPhoto && (
                      <Button
                        btnStyle="reset"
                        onClick={() => {
                          openModal(FACE_TAG_IDENTITY_SELECTION_MODAL, {
                            face,
                            currentIdentity: matchedPhoto?.identity,
                          });
                        }}
                        disabled={updating}
                        className="face-tag-photo-facebox-name"
                      >
                        {getIdentityFullName(matchedPhoto.identity)}
                      </Button>
                    )}
                  </div>
                );
              })}
            {onNextRecord && (
              <button
                className="face-tag-viewer-nav face-tag-viewer-nav-next"
                onClick={onNextRecord}
                disabled={!nextRecordId}
              >
                <span className="face-tag-viewer-nav-outline">
                  <ArrowLeft color="#fff" />
                </span>
              </button>
            )}
          </div>

          <FaceTagIdentitySelection
            albumId={albumId}
            onSelectIdentity={onSelectIdentity}
          />
        </Fragment>
      )}
    </div>
  );
};

FaceTagPhoto.propTypes = propTypes;

const withUserData = graphql(MeQuery, {
  props: ({ data }) => ({
    userId: data.me && data.me.id,
  }),
});

const withPhotoForHighlights = graphql(PhotoForFaceTag, {
  options: ({ photoId, albumId }) => ({
    variables: {
      id: photoId,
      albumIds: [albumId],
    },
    fetchPolicy: 'network-only',
  }),
  props: ({ data: { photo, loading } }) => ({
    photo,
    faces: photo?.faces.records ?? [],
    loading,
  }),
});

const withBulkVote = graphql(BulkVote, {
  props: ({ mutate, ownProps }) => ({
    bulkVote: ({
      identityId,
      faceId,
      photoId,
      userId,
      selfieId,
      // These 2 will be for updating the store. They're not required
      // as part of the input.
      identity,
      face,
      decision = VOTE_DECISION.TOTALLY,
    }) =>
      mutate({
        variables: {
          input: {
            votes: [
              {
                identity_id: identityId,
                compared_face_id: faceId,
                compared_photo_id: photoId,
                judge_id: userId,
                selfie_face_id: selfieId,
                is_consumer: false,
                applied: true,
                decision,
              },
            ],
          },
        },

        update: store => {
          const { albumId } = ownProps;

          const data = store.readQuery({
            query: PhotoForFaceTag,
            variables: {
              id: photoId,
              albumIds: [albumId],
            },
          });

          const updatedData = produce(data, draft => {
            draft.photo.faces.records = draft.photo.faces.records.map(
              faceRecord => {
                if (faceRecord.id !== face.id) {
                  return faceRecord;
                }

                return {
                  ...faceRecord,
                  has_human_votes: true,
                  matched_photos: {
                    ...faceRecord.matched_photos,
                    records:
                      decision === VOTE_DECISION.NOPE
                        ? []
                        : [
                            {
                              __typename: 'MatchedPhoto',
                              id: identity.id,
                              identity,
                            },
                          ],
                  },
                };
              },
            );
          });

          store.writeQuery({
            query: PhotoForFaceTag,
            variables: {
              id: photoId,
              albumIds: [albumId],
            },
            data: updatedData,
          });
        },
      }),
  }),
});

const withIdentityPubBoxCreate = graphql(IdentityPubBoxCreate, {
  props: ({ mutate, ownProps: { albumId } }) => ({
    identityPubBoxCreate: ({ identityId, photoId, box }) =>
      mutate({
        variables: {
          input: {
            identity_id: identityId,
            photo_id: photoId,
            box,
          },
        },
        refetchQueries: [
          {
            query: PhotoForFaceTag,
            variables: {
              id: photoId,
              albumIds: [albumId],
            },
          },
        ],
      }),
  }),
});

const mapDispatchToProps = {
  openModal: openModalAction,
  openAlert: openAlertAction,
};

export { FaceTagPhoto as FaceTagPhotoComponent };

export default compose(
  withRouter,
  connect(null, mapDispatchToProps),
  mapProps(({ match, ...props }) => ({
    ...props,
    match,
    albumId: props.albumId || match?.params.albumId,
    photoId: props.photoId || match?.params.photoId,
  })),
  withUserData,
  withPhotoForHighlights,
  withBulkVote,
  withIdentityPubBoxCreate,
)(FaceTagPhoto);
